/**
 * Pure outcome → panel-state resolution for the wallet-verification flow.
 *
 * Extracted from `useVerifyActionFlow` so the branching (and its
 * user-facing copy) is unit-testable without rendering the hook. The hook
 * owns the timers, the run-token bookkeeping, and the React state setters;
 * this module owns the decision of *which* step/error/proof each outcome
 * maps to.
 */

import type {
    VerificationErrorKind,
    VerificationMethod,
    VerificationStep
} from "@solomon-apps/shared/src/components/console/AccountOwnershipVerificationPanel";

import type { VerificationFailureReason, VerificationProof } from "./useWalletVerification";

/**
 * How long to wait for the wallet to answer before giving up on a run.
 *
 * A wallet can go permanently silent: it's locked and never prompts, or
 * its browser-extension content script was orphaned (Phantom's
 * "Extension context invalidated" after the extension updates/reloads),
 * in which case the `signMessage` promise never resolves *or* rejects.
 * Without a ceiling the panel sits on "Waiting on wallet…" forever. 60s
 * is generous enough for slow hardware-wallet confirmations and for a
 * user reading the message before signing.
 */
export const WALLET_RESPONSE_TIMEOUT_MS = 60_000;

/**
 * Shown when the wait times out. No em dash (reads as machine-written),
 * and it names the most common real-world cause + the fix.
 */
export const WALLET_NO_RESPONSE_MESSAGE =
    "Your wallet didn't respond. It may be locked, or the extension may need a refresh. Reload the page and try again.";

/**
 * Calm, non-alarming body copy for the error step. Verification has a
 * built-in fallback, so a failed signature is never framed as a platform
 * fault. Each line names the working next step.
 */
export const SIGNATURE_CANCELLED_MESSAGE =
    "You dismissed the request in your wallet. Try again, or verify with a small test transfer.";
export const COULD_NOT_SIGN_MESSAGE =
    "Some wallets, like Ledger hardware wallets, can't sign verification messages. You can verify with a tiny on-chain transfer instead (about $0.01).";
export const TRANSFER_FAILED_MESSAGE = "The test transfer didn't go through. Please try again.";
export const TRANSFER_CANCELLED_MESSAGE = "You dismissed the transfer in your wallet. Try again when you're ready.";
/**
 * Micro-transfer wait timeout. Unlike the signMessage variant, the run is
 * NOT invalidated (see `useVerifyActionFlow`): an approval that arrives
 * after this message broadcasts a real, paid transfer, so the flow stays
 * subscribed and resolves to success if the confirmation lands.
 */
export const TRANSFER_NO_RESPONSE_MESSAGE =
    "Your wallet didn't respond. If you already approved the transfer, keep this page open and we'll pick it up when it confirms. Otherwise, try again.";

/**
 * Failed-transfer copy, with the on-chain reference when the transfer was
 * actually broadcast — the user paid for that transaction; support needs
 * the signature to trace it.
 */
export function transferFailedMessage(transactionSignature: string | null | undefined): string {
    if (!transactionSignature) return TRANSFER_FAILED_MESSAGE;
    return `${TRANSFER_FAILED_MESSAGE} Transfer reference: ${transactionSignature}`;
}

export type RunOutcome =
    /** `verify()` returned a proof — the wallet signed / the transfer confirmed. */
    | { kind: "signed"; proof: VerificationProof }
    /** `verify()` failed — wallet rejected or the path failed. */
    | {
          kind: "failed";
          reason?: VerificationFailureReason;
          /** On-chain reference when a micro-transfer broadcast before failing. */
          transactionSignature?: string | null;
      }
    /** The wallet never answered within the timeout window. */
    | { kind: "timedout" };

export interface RunResolution {
    /** Next panel step. "committing" only on success; "error" otherwise. */
    step: Extract<VerificationStep, "committing" | "error">;
    error: string | null;
    proof: VerificationProof | null;
    /** Why we landed on the error step (drives the panel's CTA hierarchy);
     *  null on the success/committing path. */
    errorKind: VerificationErrorKind | null;
}

/**
 * Map a run's terminal outcome to the panel state the hook should apply.
 * The success path stops at "committing"; the hook handles the brief
 * committing → success transition itself (a timing concern, not a
 * decision).
 *
 * `errorKind` lets the panel lead with the action that will actually work:
 * a genuine cancel keeps "Try again" primary; a sign that the wallet
 * couldn't produce (hardware wallets, no response) leads with the
 * micro-transfer instead.
 */
export function resolveRunOutcome(outcome: RunOutcome, method: VerificationMethod): RunResolution {
    if (outcome.kind === "signed") {
        return { step: "committing", error: null, proof: outcome.proof, errorKind: null };
    }

    if (outcome.kind === "timedout") {
        return {
            step: "error",
            error: method === "microTransfer" ? TRANSFER_NO_RESPONSE_MESSAGE : WALLET_NO_RESPONSE_MESSAGE,
            proof: null,
            errorKind: "noResponse"
        };
    }

    // A micro-transfer that failed has no further fallback to offer; a
    // deliberate cancel keeps "Try again" framing, anything else retries
    // the transfer itself (with the on-chain reference when one exists).
    if (method === "microTransfer") {
        if (outcome.reason === "cancelled") {
            return { step: "error", error: TRANSFER_CANCELLED_MESSAGE, proof: null, errorKind: "cancelled" };
        }
        return {
            step: "error",
            error: transferFailedMessage(outcome.transactionSignature),
            proof: null,
            errorKind: "transferFailed"
        };
    }

    // signMessage failed. A deliberate cancel keeps retry primary; anything
    // else (e.g. a hardware wallet that can't sign messages) routes to the
    // micro-transfer.
    const cancelled = outcome.reason === "cancelled";
    return {
        step: "error",
        error: cancelled ? SIGNATURE_CANCELLED_MESSAGE : COULD_NOT_SIGN_MESSAGE,
        proof: null,
        errorKind: cancelled ? "cancelled" : "couldNotSign"
    };
}
